(() => {
  if (window.RCBundleCartInitialized) return;
  window.RCBundleCartInitialized = true;
  document.documentElement.classList.add('rc-bundle-cart-ready');

  const shopRoot = window.Shopify?.routes?.root || '/';
  const giftTypes = ['ebook', 'notebook'];
  const isCartPage = () => {
    const normalizedRoot = shopRoot.replace(/\/+$/, '');
    const normalizedPath = window.location.pathname.replace(/\/+$/, '');
    return normalizedPath === `${normalizedRoot}/cart`;
  };
  let syncing = false;
  let syncQueued = false;

  const storageGet = (key) => {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const storageSet = (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // Storage can be unavailable in privacy modes.
    }
  };

  const storageRemove = (key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      // Storage can be unavailable in privacy modes.
    }
  };

  const celebrate = (detail = {}) => {
    if (detail.type !== 'notebook') return;
    const celebratedKey = 'rcGiftCelebrated:notebook';
    if (storageGet(celebratedKey) === 'true') return;
    storageSet(celebratedKey, 'true');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.documentElement.classList.add('rc-bundle-confetti-fired');

    const colors = ['#3f8547', '#f3c400', '#171717', '#e8b4c7', '#9fcaac', '#ffffff'];
    const layer = document.createElement('div');
    layer.className = 'rc-bundle-confetti';
    layer.setAttribute('aria-hidden', 'true');

    for (let index = 0; index < 38; index += 1) {
      const particle = document.createElement('span');
      const angle = (-165 + Math.random() * 150) * (Math.PI / 180);
      const distance = 130 + Math.random() * 260;
      particle.className = 'rc-bundle-confetti__particle';
      particle.style.setProperty('--rc-confetti-color', colors[index % colors.length]);
      particle.style.setProperty('--rc-confetti-x', `${Math.cos(angle) * distance}px`);
      particle.style.setProperty('--rc-confetti-y', `${Math.sin(angle) * distance + 170}px`);
      particle.style.setProperty('--rc-confetti-rotation', `${Math.round(Math.random() * 720 - 360)}deg`);
      particle.style.setProperty('--rc-confetti-duration', `${900 + Math.round(Math.random() * 500)}ms`);
      particle.style.setProperty('--rc-confetti-width', `${5 + Math.round(Math.random() * 4)}px`);
      particle.style.setProperty('--rc-confetti-height', `${7 + Math.round(Math.random() * 7)}px`);
      layer.appendChild(particle);
    }

    document.body.appendChild(layer);
    window.setTimeout(() => layer.remove(), 1700);
  };

  document.addEventListener('rc:gift-unlocked', (event) => celebrate(event.detail));
  (window.rcGiftUnlockQueue || []).forEach((detail) => celebrate(detail));
  window.rcGiftUnlockQueue = [];

  const getCart = async () => {
    const response = await fetch(`${shopRoot}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Cart could not be loaded');
    return response.json();
  };

  const updateCart = async (updates) => {
    const response = await fetch(`${shopRoot}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!response.ok) throw new Error('Cart could not be updated');
    return response.json();
  };

  const addGift = async (id, type, metadata) => {
    const properties = {
      _bundle_group: metadata.group || 'rc-bundle',
      _bundle_template: 'bundle',
      _rc_bundle_gift: type,
      _gift_limit: '1',
    };
    if (type === 'notebook') properties._threshold_amount = String(metadata.threshold || 7500);

    const response = await fetch(`${shopRoot}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity: 1, properties }] }),
    });
    if (!response.ok) throw new Error('Gift could not be added');
    return response.json();
  };

  const giftItemsFor = (cart, type) => cart.items.filter((item) => item.properties?._rc_bundle_gift === type);

  const giftRewardAllocation = (item) => (item.line_level_discount_allocations || []).reduce((sum, allocation) => {
    const title = String(allocation.discount_application?.title || '').toLowerCase();
    return title === 'bundle-geschenk-notizbuch-ab-75'
      ? sum + (Number(allocation.amount) || 0)
      : sum;
  }, 0);

  const qualifyingCartTotal = (cart) => cart.items.reduce((sum, item) => {
    if (giftTypes.includes(item.properties?._rc_bundle_gift)) return sum;

    // Shopify distributes an order-level gift discount across the paid lines.
    // Add that allocation back only for threshold calculation, otherwise the
    // discount itself can make the cart fall below 75 EUR and remove its gift.
    return sum + (item.final_line_price || 0) + giftRewardAllocation(item);
  }, 0);

  const getBundleMetadata = (cart) => {
    const bundleItem = cart.items.find((item) => item.properties?._bundle_template === 'bundle' && !item.properties?._rc_bundle_gift);
    if (!bundleItem) return null;
    const properties = bundleItem.properties || {};
    return {
      group: properties._bundle_group || 'rc-bundle',
      includedGiftId: Number(properties._included_gift_variant) || 0,
      includedAutoAdd: properties._included_gift_auto_add === 'true',
      thresholdGiftId: Number(properties._threshold_gift_variant) || 0,
      thresholdAutoAdd: properties._threshold_gift_auto_add === 'true',
      threshold: Number(properties._threshold_amount) || 7500,
      confettiEnabled: properties._gift_confetti_enabled !== 'false',
    };
  };

  const finishSync = async (changed) => {
    if (!changed) return;
    const freshCart = await getCart();
    if (isCartPage()) {
      window.location.reload();
      return;
    }
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { cart: freshCart },
    }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { cart: freshCart },
    }));
  };

  const syncCart = async (providedCart) => {
    if (syncing) {
      syncQueued = true;
      return;
    }
    syncing = true;
    let changed = false;

    try {
      const cart = providedCart?.items ? providedCart : await getCart();
      const metadata = getBundleMetadata(cart);
      const ebookItems = giftItemsFor(cart, 'ebook');
      const notebookItems = giftItemsFor(cart, 'notebook');
      const qualifyingTotal = Math.max(0, qualifyingCartTotal(cart));
      const rewardsMeter = document.querySelector('.rc-cart-rewards[data-rc-gift-threshold]');
      const visibleGiftThreshold = Number(rewardsMeter?.dataset.rcGiftThreshold) || 7500;
      const thresholdReached = Boolean(rewardsMeter && qualifyingTotal >= visibleGiftThreshold);
      document.documentElement.classList.toggle('rc-bundle-threshold-reached', thresholdReached);
      const thresholdStateKey = 'rcGiftThresholdReached:notebook';
      const celebrationPendingKey = 'rcGiftCelebrationPending:notebook';
      const wasThresholdReached = storageGet(thresholdStateKey) === 'true';
      const updates = {};

      if (thresholdReached && !wasThresholdReached && metadata?.confettiEnabled !== false) {
        storageSet(thresholdStateKey, 'true');
        storageSet(celebrationPendingKey, 'true');
      } else if (!thresholdReached) {
        storageRemove(thresholdStateKey);
        storageRemove(celebrationPendingKey);
      }

      if (!metadata) {
        [...ebookItems, ...notebookItems].forEach((item) => { updates[item.key] = 0; });
        giftTypes.forEach((type) => storageRemove(`rcGiftDeclined:${type}`));
      } else {
        ebookItems.forEach((item, index) => { updates[item.key] = index === 0 ? 1 : 0; });
        notebookItems.forEach((item, index) => {
          updates[item.key] = index === 0 && qualifyingTotal >= metadata.threshold ? 1 : 0;
        });
      }

      if (Object.keys(updates).some((key) => {
        const item = cart.items.find((cartItem) => cartItem.key === key);
        return item && item.quantity !== updates[key];
      })) {
        await updateCart(updates);
        changed = true;
      }

      if (metadata) {
        const ebookDeclined = storageGet('rcGiftDeclined:ebook') === 'true';
        const notebookDeclined = storageGet('rcGiftDeclined:notebook') === 'true';
        if (metadata.includedAutoAdd && metadata.includedGiftId && ebookItems.length === 0 && !ebookDeclined) {
          await addGift(metadata.includedGiftId, 'ebook', metadata);
          changed = true;
        }
        if (metadata.thresholdAutoAdd && metadata.thresholdGiftId && qualifyingTotal >= metadata.threshold && notebookItems.length === 0 && !notebookDeclined) {
          await addGift(metadata.thresholdGiftId, 'notebook', metadata);
          changed = true;
        }
      }

      await finishSync(changed);
      const cartPageWillReload = changed && isCartPage();
      if (!cartPageWillReload && thresholdReached && storageGet(celebrationPendingKey) === 'true') {
        storageRemove(celebrationPendingKey);
        celebrate({ type: 'notebook' });
      }
    } catch (error) {
      console.warn('[RC Bundle] Gift synchronization skipped:', error);
    } finally {
      syncing = false;
      if (syncQueued) {
        syncQueued = false;
        window.setTimeout(() => syncCart(), 0);
      }
    }
  };

  document.addEventListener('click', (event) => {
    const removeLink = event.target.closest('[data-rc-gift-type] .line-item__remove-button');
    if (!removeLink) return;
    const giftLine = removeLink.closest('[data-rc-gift-type]');
    const type = giftLine?.dataset.rcGiftType;
    if (giftTypes.includes(type)) storageSet(`rcGiftDeclined:${type}`, 'true');
  }, true);

  const scheduleCartSync = () => {
    window.setTimeout(() => syncCart(), 250);
    window.setTimeout(() => syncCart(), 900);
  };

  document.addEventListener('click', (event) => {
    if (!isCartPage() && event.target.closest('line-item-quantity a, .line-item__remove-button')) {
      scheduleCartSync();
    }
  }, true);

  document.addEventListener('change', (event) => {
    if (!isCartPage() && event.target.closest('line-item-quantity input')) {
      scheduleCartSync();
    }
  }, true);

  document.documentElement.addEventListener('cart:updated', (event) => syncCart(event.detail?.cart));
  document.addEventListener('bundle:added', () => window.setTimeout(() => syncCart(), 50));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncCart(), { once: true });
  } else {
    syncCart();
  }
})();
